import { create } from 'zustand'

export type StreamGoalResponse = {
  stream_id: string
  user_id: string
  goal_id?: string
  amount_goal: number | null
  amount_tipped: number
  remaining: number | null
  percent: number | null
  public?: boolean
  completed?: boolean
  reward?: string | null
}

export enum SETTINGS {
  DEFAULT,
  CAMERA,
  AVAILABILITY,
  CONNECTION,
  SORT_MODAL
}

export enum VIDEO_ORIENTATION {
  PORTRAIT,
  LANDSCAPE
}

interface TipModalData {
  goal: string
  reward: string
  showGoal: boolean
}

export interface MediaStreamOptions {
  label: string
  deviceId: string
}

interface Group {
  id: string
  name: string
}

interface StreamingStore {
  isStreaming: boolean
  muted: boolean
  commentsActive: boolean
  startModalOpen: boolean
  viewersModalOpen: boolean
  tipModalOpen: boolean
  endStatsModalOpen: boolean
  endModalOpen: boolean
  settingsType: SETTINGS
  videoOrientation: VIDEO_ORIENTATION
  tipModalData: TipModalData
  description: string
  cameraList: MediaStreamOptions[]
  microphoneList: MediaStreamOptions[]
  activeCamera: MediaStreamOptions | null
  activeMicrophone: MediaStreamOptions | null
  groups: Group[]
  showTips: boolean
  tipsAmount: number

  streamId: string | null
  currentStreamGoal: StreamGoalResponse | null
  viewerCount: number

  setStreamId: (streamId: string | null) => void
  clearStreamId: () => void

  setCurrentStreamGoal: (goal: StreamGoalResponse | null) => void
  clearCurrentStreamGoal: () => void
  incrementCurrentStreamGoalTipped: (amount: number) => void

  setViewerCount: (count: number) => void
  incrementViewerCount: () => void
  decrementViewerCount: () => void
  clearViewerCount: () => void

  setTipsAmount: (amount: number) => void
  incrementTipsAmount: (amount: number) => void
  clearTipsAmount: () => void

  startStreamingHandler: () => void
  mute: () => void
  unmute: () => void
  toggleCommentsActive: () => void
  openViewersModal: () => void
  closeViewersModal: () => void
  openTipModal: () => void
  closeTipModal: () => void
  openEndModal: () => void
  closeEndModal: () => void
  confirmHandler: () => void
  setSettingsType: (type: SETTINGS) => void
  setTipModalData: (data: TipModalData) => void
  setDescription: (data: string) => void
  setCameraList: (data: MediaStreamOptions[]) => void
  setMicrophoneList: (data: MediaStreamOptions[]) => void
  setActiveCamera: (data: MediaStreamOptions) => void
  setActiveMicrophone: (data: MediaStreamOptions) => void
  setGroups: (data: Group[]) => void
  setShowTips: (value: boolean) => void
  dismissSummary: () => void
  resetForCreateView: () => void
}

export const useStreamingStore = create<StreamingStore>()(set => ({
  isStreaming: false,
  muted: false,
  description: '',
  cameraList: [],
  microphoneList: [],
  commentsActive: false,
  startModalOpen: true,
  viewersModalOpen: false,
  tipModalOpen: false,
  endStatsModalOpen: false,
  endModalOpen: false,
  settingsType: SETTINGS.DEFAULT,
  videoOrientation: VIDEO_ORIENTATION.PORTRAIT,
  activeCamera: null,
  activeMicrophone: null,
  groups: [{ id: 'all', name: 'All' }],
  showTips: true,
  tipsAmount: 0,

  streamId: null,
  currentStreamGoal: null,
  viewerCount: 0,

  tipModalData: {
    goal: '',
    reward: '',
    showGoal: false,
  },

  setStreamId: (streamId: string | null) => set({ streamId }),
  clearStreamId: () => set({ streamId: null }),

  setCurrentStreamGoal: (goal: StreamGoalResponse | null) =>
    set({ currentStreamGoal: goal }),

  clearCurrentStreamGoal: () => set({ currentStreamGoal: null }),

  incrementCurrentStreamGoalTipped: (amount: number) =>
    set(state => {
      const current = state.currentStreamGoal

      if (!current || !Number.isFinite(amount) || amount <= 0) {
        return state
      }

      const nextAmountTipped = Number(current.amount_tipped ?? 0) + amount
      const amountGoal =
        current.amount_goal != null ? Number(current.amount_goal) : null

      const remaining =
        amountGoal != null ? Math.max(amountGoal - nextAmountTipped, 0) : null

      const percent =
        amountGoal != null && amountGoal > 0
          ? Math.min((nextAmountTipped / amountGoal) * 100, 100)
          : null

      return {
        currentStreamGoal: {
          ...current,
          amount_tipped: nextAmountTipped,
          remaining,
          percent,
        },
      }
    }),

  setViewerCount: (count: number) =>
    set({ viewerCount: Number.isFinite(count) ? Math.max(0, count) : 0 }),

  incrementViewerCount: () =>
    set(state => ({
      viewerCount: state.viewerCount + 1,
    })),

  decrementViewerCount: () =>
    set(state => ({
      viewerCount: Math.max(0, state.viewerCount - 1),
    })),

  clearViewerCount: () => set({ viewerCount: 0 }),

  setTipsAmount: (amount: number) =>
    set({ tipsAmount: Number.isFinite(amount) ? Math.max(0, amount) : 0 }),

  incrementTipsAmount: (amount: number) =>
    set(state => ({
      tipsAmount:
        Number.isFinite(amount) && amount > 0
          ? state.tipsAmount + amount
          : state.tipsAmount,
    })),

  clearTipsAmount: () => set({ tipsAmount: 0 }),

  startStreamingHandler: () =>
    set({
      isStreaming: true,
      startModalOpen: false,
    }),

  mute: () => set({ muted: true }),
  unmute: () => set({ muted: false }),

  toggleCommentsActive: () =>
    set(state => ({ commentsActive: !state.commentsActive })),

  openViewersModal: () => set({ viewersModalOpen: true }),
  closeViewersModal: () => set({ viewersModalOpen: false }),

  openTipModal: () => set({ tipModalOpen: true }),
  closeTipModal: () => set({ tipModalOpen: false }),

  openEndModal: () => set({ endModalOpen: true }),
  closeEndModal: () => set({ endModalOpen: false }),

  confirmHandler: () =>
    set({
      endStatsModalOpen: true,
      endModalOpen: false,
      isStreaming: false,
      streamId: null,
      currentStreamGoal: null,
      viewerCount: 0,
      tipsAmount: 0,
    }),

  dismissSummary: () =>
    set({
      endStatsModalOpen: false,
      startModalOpen: false,
      endModalOpen: false,
      commentsActive: false,
    }),

  resetForCreateView: () =>
    set({
      isStreaming: false,
      startModalOpen: true,
      endStatsModalOpen: false,
      endModalOpen: false,
      commentsActive: false,
      settingsType: SETTINGS.DEFAULT,
      streamId: null,
      currentStreamGoal: null,
      viewerCount: 0,
      showTips: true,
      tipsAmount: 0,
    }),

  setSettingsType: (type: SETTINGS) => set({ settingsType: type }),
  setTipModalData: (data: TipModalData) => set({ tipModalData: data }),
  setDescription: (data: string) => set({ description: data }),
  setCameraList: (data: MediaStreamOptions[]) => set({ cameraList: data }),
  setMicrophoneList: (data: MediaStreamOptions[]) =>
    set({ microphoneList: data }),
  setActiveCamera: (data: MediaStreamOptions) => set({ activeCamera: data }),
  setActiveMicrophone: (data: MediaStreamOptions) =>
    set({ activeMicrophone: data }),
  setGroups: (data: Group[]) => set({ groups: data }),
  setShowTips: (value: boolean) => set({ showTips: value }),
}))